import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Phone, Loader2, ImagePlus, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, SERVICES } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROPERTY_TYPES = ["Homeowner", "Landlord", "Rental Property Manager"];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  property_type: PROPERTY_TYPES[0],
  service_category: SERVICES[0].title,
  message: "",
};

const inputCls =
  "w-full rounded-2xl border border-stone-200 bg-white px-5 py-3.5 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10";

export default function QuoteForm() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const fileInputRef = useRef(null);

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    e.target.value = "";
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 8 MB)`);
        continue;
      }
      const tempId = `${Date.now()}-${file.name}`;
      const preview = URL.createObjectURL(file);
      setPhotos((p) => [...p, { tempId, preview, path: null, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/uploads`, fd);
        setPhotos((p) =>
          p.map((ph) => (ph.tempId === tempId ? { ...ph, path: res.data.path, uploading: false } : ph))
        );
      } catch (err) {
        setPhotos((p) => p.filter((ph) => ph.tempId !== tempId));
        toast.error("Photo upload failed", {
          description: err.response?.data?.detail || "Please try a JPG or PNG under 8 MB.",
        });
      }
    }
  };

  const removePhoto = (tempId) =>
    setPhotos((p) => p.filter((ph) => ph.tempId !== tempId));

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  useEffect(() => {
    const onEstimate = (e) => {
      const { service, sizeLabel, range } = e.detail;
      setForm((f) => ({
        ...f,
        service_category: service,
        message: `Instant estimate selected: ${service} — ${sizeLabel} (${range}). Job details: ${f.message.replace(/^Instant estimate selected:.*?Job details: /, "")}`,
      }));
      toast.info("Estimate added to your quote", {
        description: `${service} · ${sizeLabel} · ${range}`,
      });
    };
    window.addEventListener("acpm:estimate", onEstimate);
    return () => window.removeEventListener("acpm:estimate", onEstimate);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (photos.some((p) => p.uploading)) {
      toast.info("Photos are still uploading — one moment.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, photo_paths: photos.map((p) => p.path).filter(Boolean) };
      const res = await axios.post(`${API}/leads`, payload);
      if (res.data.status === "success") {
        toast.success("Quote request sent", {
          description: "Michael will get back to you within 2 business hours.",
        });
        setForm(initialForm);
        setPhotos([]);
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      toast.error("Something went wrong", {
        description: `Please try again or call Michael directly at ${PHONE_DISPLAY}.`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-slate-900 py-24 text-white sm:py-32">
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 sm:px-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono-accent mb-4 text-xs uppercase tracking-[0.24em] text-amber-500">
            Get Started
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Request a{" "}
            <span className="font-serif-accent font-medium italic text-slate-400">Fast Quote</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
            Fill out the quick form or call Michael directly. Expect a response
            within 2 business hours — often sooner for Broward & Palm Beach.
          </p>
          <a
            data-testid="quote-call-link"
            href={PHONE_TEL}
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold transition-colors duration-300 hover:border-amber-500/50"
          >
            <Phone className="h-4 w-4 text-amber-500" />
            {PHONE_DISPLAY}
          </a>
          <p className="font-mono-accent mt-6 text-[11px] uppercase tracking-[0.18em] text-slate-500">
            Mon–Sat 7:30 AM – 6:00 PM · Sun emergency dispatch
          </p>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
          className="rounded-[2rem] bg-[#FAF9F6] p-7 text-slate-900 shadow-2xl sm:p-10"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="quote-name" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Full Name
              </label>
              <input
                id="quote-name"
                data-testid="contact-form-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Rivera"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="quote-phone" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Phone
              </label>
              <input
                id="quote-phone"
                data-testid="contact-form-phone-input"
                required
                minLength={7}
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="(954) 555-0100"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="quote-email" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Email
              </label>
              <input
                id="quote-email"
                data-testid="contact-form-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="quote-property-type" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                I am a
              </label>
              <select
                id="quote-property-type"
                data-testid="contact-form-property-type-select"
                value={form.property_type}
                onChange={set("property_type")}
                className={inputCls}
              >
                {PROPERTY_TYPES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="quote-service" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Service Needed
              </label>
              <select
                id="quote-service"
                data-testid="contact-form-service-select"
                value={form.service_category}
                onChange={set("service_category")}
                className={inputCls}
              >
                {SERVICES.map((s) => (
                  <option key={s.number} value={s.title}>{s.title}</option>
                ))}
                <option value="Something Else">Something Else</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="quote-message" className="font-mono-accent mb-2 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Tell me about the job
              </label>
              <textarea
                id="quote-message"
                data-testid="contact-form-message-input"
                required
                minLength={5}
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="e.g. Two drywall patches in the guest bath, plus a sticking deadbolt on the back door…"
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          <div className="mt-5">
            <p className="font-mono-accent mb-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">
              Photos of the job <span className="normal-case tracking-normal text-slate-400">(optional · up to 3)</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              multiple
              hidden
              data-testid="contact-form-photo-input"
              onChange={onFilesSelected}
            />
            <div className="flex flex-wrap items-center gap-3">
              {photos.map((ph) => (
                <div
                  key={ph.tempId}
                  data-testid="contact-form-photo-thumb"
                  className="relative h-20 w-20 overflow-hidden rounded-xl border border-stone-200 bg-stone-100"
                >
                  <img src={ph.preview} alt="attachment preview" className="h-full w-full object-cover" />
                  {ph.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="contact-form-photo-remove"
                    onClick={() => removePhoto(ph.tempId)}
                    className="absolute right-1 top-1 rounded-full bg-slate-900/80 p-1 text-white transition-colors hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <button
                  type="button"
                  data-testid="contact-form-add-photo-button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-stone-300 text-slate-400 transition-colors duration-200 hover:border-amber-500 hover:text-amber-600"
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-[10px] font-semibold">Add</span>
                </button>
              )}
            </div>
          </div>

          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={loading}
            className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-amber-600 hover:shadow-xl hover:shadow-amber-600/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send Quote Request
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
          <p className="mt-4 text-center text-xs text-slate-400">
            Written scope & transparent pricing before any work begins.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
